import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import './WikiItemDetail.css';
import api from '../API/Api';
import { McIcon } from '../components/CraftingGrid';

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const TAGS = [
  { value: 'balance',     label: 'Balance',     color: '#f0ab20' },
  { value: 'bugfix',      label: 'Bugfix',      color: '#e84c3d' },
  { value: 'new_content', label: 'New Content', color: '#52c078' },
  { value: 'mechanic',    label: 'Mechanic',    color: '#5a9ee0' },
  { value: 'rework',      label: 'Rework',      color: '#a070e0' },
  { value: 'removed',     label: 'Removed',     color: '#9e9e9e' },
  { value: 'custom',      label: 'Notes',       color: '#ccc' },
];

const CHANGE_TYPE_ICONS = {
  new_item: 'diamond_sword',  update_item: 'anvil',
  new_mob: 'zombie_head',     update_mob: 'zombie_head',
  supply_drop: 'chest',       new_mechanic: 'piston',
  edit_mechanic: 'piston',    new_feature: 'nether_star',
  removed_item: 'barrier',    removed_mob: 'barrier',
  custom: 'book',
};

const IMPACT_COLOR = { minor: '#9e9e9e', major: '#f0ab20', rework: '#e84c3d' };
const STATUS_COLOR = { published: '#52c078', rolled_back: '#9e9e9e', draft: '#5a9ee0' };

function tagInfo(tag) {
  return TAGS.find(t => t.value === tag) || { label: tag, color: '#ccc' };
}

function DiffTable({ before, after }) {
  if (!before && !after) return null;
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) return null;
  return (
    <table style={{
      width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginTop: '0.6rem',
    }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, padding: '0.3rem 0.5rem', width: '30%' }}>Field</th>
          <th style={{ textAlign: 'left', color: '#e84c3d', fontWeight: 600, padding: '0.3rem 0.5rem', width: '35%' }}>Before</th>
          <th style={{ textAlign: 'left', color: '#52c078', fontWeight: 600, padding: '0.3rem 0.5rem', width: '35%' }}>After</th>
        </tr>
      </thead>
      <tbody>
        {keys.map(k => (
          <tr key={k} style={{ borderTop: '1px solid var(--border)' }}>
            <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{k}</td>
            <td style={{ padding: '0.3rem 0.5rem', color: '#e84c3d', background: 'rgba(232,76,61,0.06)' }}>
              {before?.[k] !== undefined ? String(before[k]) : '—'}
            </td>
            <td style={{ padding: '0.3rem 0.5rem', color: '#52c078', background: 'rgba(82,192,120,0.06)' }}>
              {after?.[k] !== undefined ? String(after[k]) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EntryRow({ entry }) {
  const icon  = CHANGE_TYPE_ICONS[entry.change_type] || 'book';
  const tInfo = tagInfo(entry.tag);

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8,
      padding: '0.9rem 1rem', background: 'var(--bg-card)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem' }}>
        <div style={{
          width: 32, height: 32, borderRadius: 6, flexShrink: 0,
          background: `${tInfo.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${tInfo.color}33`,
        }}>
          <McIcon materialKey={icon} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            {entry.entity_name && (
              entry.entity_link
                ? <Link to={entry.entity_link} style={{ fontWeight: 700, color: 'var(--teal)', textDecoration: 'none', fontSize: '0.9rem' }}>
                    {entry.entity_name}
                  </Link>
                : <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.9rem' }}>{entry.entity_name}</span>
            )}
            <span style={{
              background: `${tInfo.color}1a`, color: tInfo.color,
              border: `1px solid ${tInfo.color}33`, borderRadius: 4,
              padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600,
            }}>
              {tInfo.label}
            </span>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.55 }}>
            {entry.summary}
          </p>
          {(entry.before_data || entry.after_data) && (
            <DiffTable before={entry.before_data} after={entry.after_data} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function WikiUpdateDetail() {
  const { versionId } = useParams();
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!versionId) return;
    api('getversiondetail', { version_id: Number(versionId) }).then(res => {
      if (res.isSuccess && res.data) {
        setVersion(res.data);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    });
  }, [versionId]);

  if (loading) {
    return (
      <div className="wiki-detail-root">
        <div style={{ padding: '8rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading changelog...
        </div>
      </div>
    );
  }

  if (notFound || !version) {
    return (
      <div className="wiki-detail-root">
        <div style={{ padding: '8rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Version not found.{' '}
          <Link to="/wiki/updates" style={{ color: 'var(--teal)' }}>← Back to updates</Link>
        </div>
      </div>
    );
  }

  const entries    = version.entries || [];
  const impactC    = IMPACT_COLOR[version.impact] || '#9e9e9e';
  const statusC    = STATUS_COLOR[version.status] || '#9e9e9e';

  // group entries by tag
  const grouped = TAGS.reduce((acc, t) => {
    const list = entries.filter(e => e.tag === t.value);
    if (list.length) acc.push({ ...t, entries: list });
    return acc;
  }, []);

  // also catch any entries with tags not in TAGS list
  const knownTags = new Set(TAGS.map(t => t.value));
  const orphans   = entries.filter(e => !knownTags.has(e.tag));
  if (orphans.length) grouped.push({ value: 'other', label: 'Other', color: '#ccc', entries: orphans });

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <div className="wiki-detail-hero-overlay" />
        {version.video_url ? (
          <video
            src={version.video_url}
            autoPlay loop muted playsInline
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', opacity: 0.35,
            }}
          />
        ) : version.image_url ? (
          <img src={version.image_url} alt="" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: 0.25,
          }} />
        ) : null}
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <Link to="/wiki/updates" className="wiki-back-btn">← All Updates</Link>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': impactC }}>
              <McIcon materialKey="book" size={48} />
            </div>
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{
                  background: `${impactC}22`, color: impactC, border: `1px solid ${impactC}44`,
                  borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                  {(version.impact || 'minor').toUpperCase()}
                </span>
                <span style={{
                  background: `${statusC}22`, color: statusC, border: `1px solid ${statusC}44`,
                  borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                  {version.status || 'draft'}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', alignSelf: 'center' }}>
                  {version.type} · {version.version}
                </span>
              </div>
              <h1 className="wiki-detail-item-name" style={{ color: 'var(--text)' }}>{version.title}</h1>
              <div className="wiki-detail-item-type" style={{ color: 'var(--text-muted)' }}>
                {fmtDate(version.date)} · {entries.length} change{entries.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <div className="wiki-detail-grid">
          <StaggerItem className="wiki-detail-main">
            {version.summary && (
              <div className="wiki-detail-box" style={{ marginBottom: '1.5rem' }}>
                <span className="wiki-detail-label">Overview</span>
                <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.7, fontSize: '0.95rem' }}>
                  {version.summary}
                </p>
              </div>
            )}

            {entries.length === 0 ? (
              <div className="wiki-detail-box">
                <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
                  No changelog entries for this version yet.
                </div>
              </div>
            ) : (
              grouped.map(group => (
                <div key={group.value} style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: 3, height: 18, borderRadius: 2, background: group.color }} />
                    <span style={{ fontWeight: 700, color: group.color, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {group.label}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {group.entries.length} change{group.entries.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {group.entries.map(e => <EntryRow key={e.id} entry={e} />)}
                  </div>
                </div>
              ))
            )}
          </StaggerItem>

          <StaggerItem className="wiki-detail-sidebar">
            <div>
              <span className="wiki-detail-label">Version Info</span>
              <div className="wiki-detail-box" style={{ display: 'grid', gap: '0.6rem', fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Version</span>
                  <span style={{ fontWeight: 700 }}>{version.version}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Date</span>
                  <span>{fmtDate(version.date)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Type</span>
                  <span style={{ textTransform: 'capitalize' }}>{version.type}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Impact</span>
                  <span style={{ color: impactC, fontWeight: 700, textTransform: 'capitalize' }}>{version.impact}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Changes</span>
                  <span>{entries.length}</span>
                </div>
              </div>
            </div>

            {grouped.length > 0 && (
              <div style={{ marginTop: '1.25rem' }}>
                <span className="wiki-detail-label">Contents</span>
                <div className="wiki-detail-box" style={{ display: 'grid', gap: '0.5rem' }}>
                  {grouped.map(g => (
                    <div key={g.value} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: g.color, fontWeight: 600 }}>{g.label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{g.entries.length}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: '1.25rem' }}>
              <span className="wiki-detail-label">Quick Links</span>
              <div className="wiki-detail-box" style={{ display: 'grid', gap: '0.65rem' }}>
                <Link to="/wiki/updates" style={{ color: 'var(--teal)', textDecoration: 'none', fontSize: '0.85rem' }}>← All Updates</Link>
                <Link to="/wiki/items" style={{ color: 'var(--teal)', textDecoration: 'none', fontSize: '0.85rem' }}>→ Browse Items</Link>
                <Link to="/wiki/mobs" style={{ color: 'var(--teal)', textDecoration: 'none', fontSize: '0.85rem' }}>→ Browse Mobs</Link>
              </div>
            </div>
          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
